<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import {RouterView, useRoute} from 'vue-router'
import {storeToRefs} from 'pinia'
import moment from 'moment'
import {SidebarInset, SidebarProvider, SidebarTrigger} from '@/components/ui/sidebar'
import {Toaster} from '@/components/ui/toast'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Switch} from '@/components/ui/switch'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Label} from '@/components/ui/label'
import {Button} from '@/components/ui/button'
import TimeZoneCombobox from '@/components/TimeZoneCombobox.vue'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import {useUserStore} from '@/stores/user'
import {CardsFloatingControlSizes, useLocalStore} from '@/stores/local'
import i18next from 'i18next'
import {Language} from '@shared/types'
import {ArrowLeft} from 'lucide-vue-next'
import {useHeaderStore} from '@/stores/header'
import {isThemeMode, ThemeModes} from '@/lib/theme'
const route = useRoute()
const userStore = useUserStore()
const localStore = useLocalStore()
const headerStore = useHeaderStore()
const {user, isLoading} = storeToRefs(userStore)
const showSettings = ref(false)
const showLearningNav = computed(() => !route.path.startsWith('/cp'))
const isAdmin = computed(() => user.value !== null && ['manager', 'admin'].includes(user.value.role))
const isAuthRoute = computed(() => route.path === '/auth')
const activeTab = ref<'general' | 'cards'>('cards')
const interfaceLanguages = [Language.English, Language.Russian]
const interfaceLanguage = computed(() => user.value?.settings?.interface_language ?? i18next.language)
type TimeZoneOption = {value: string; label: string; offsetMinutes: number}

function timeZoneOffsetMinutes(timeZone: string, date = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {timeZone, timeZoneName: 'longOffset'}).formatToParts(date)
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT+00:00'
    const match = offset.match(/^GMT([+-])(\d{2}):(\d{2})$/u)
    if (!match) return 0
    const minutes = Number(match[2]) * 60 + Number(match[3])
    return match[1] === '+' ? minutes : -minutes
  } catch {
    return 0
  }
}

function formatTimeZoneOffset(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absoluteMinutes = Math.abs(offsetMinutes)
  const hours = Math.floor(absoluteMinutes / 60)
  const minutes = absoluteMinutes % 60
  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const timeZoneOptions = (() => {
  const supportedValuesOf = (Intl as typeof Intl & {supportedValuesOf?: (key: string) => string[]}).supportedValuesOf
  const values = typeof supportedValuesOf === 'function' ? supportedValuesOf('timeZone') : []
  return ['UTC', ...values.filter((value) => value !== 'UTC')]
    .map((value): TimeZoneOption => {
      const offsetMinutes = timeZoneOffsetMinutes(value)
      return {
        value,
        offsetMinutes,
        label: `${formatTimeZoneOffset(offsetMinutes)} — ${value}`,
      }
    })
    .sort((left, right) => left.offsetMinutes - right.offsetMinutes || left.value.localeCompare(right.value))
})()
const timeZone = computed(() => user.value?.settings?.timezone ?? 'UTC')
const emailDigestPaused = computed(() => user.value?.settings?.email_digest_paused ?? false)

function openSettings() {
  activeTab.value = isAdmin.value ? 'general' : 'cards'
  showSettings.value = true
}
function updateFloatingControlsSize(value: unknown): void {
  if (typeof value !== 'string') return
  const size = CardsFloatingControlSizes.find((candidate) => String(candidate) === value)
  if (size !== undefined) localStore.cardsFloatingControlsSize = size
}

function updateThemeMode(value: unknown): void {
  if (isThemeMode(value)) localStore.theme = value
}

function updateTimeZone(value: unknown): void {
  if (typeof value === 'string') void userStore.updateUserSettings({timezone: value})
}

function updateEmailDigestPaused(value: boolean): void {
  void userStore.updateUserSettings({email_digest_paused: value})
}

watch(
  () => user.value?.settings?.timezone,
  (value) => {
    if (user.value && !value) {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      void userStore.updateUserSettings({timezone: detected}).catch(() => undefined)
    }
  },
  {immediate: true}
)
</script>
<template>
  <Toaster />
  <RouterView v-if="isAuthRoute" />
  <div v-else-if="!isLoading" class="h-dvh overflow-hidden bg-background text-foreground">
    <SidebarProvider class="h-full min-h-0">
      <AppSidebar :show-learning-nav="showLearningNav" @settings="openSettings" /><SidebarInset
        class="h-full min-h-0 min-w-0">
        <header
          class="sticky top-0 z-40 grid h-12 shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 border-b bg-background/95 px-2 backdrop-blur">
          <div class="relative z-10 flex min-w-0 items-center gap-2">
            <SidebarTrigger aria-label="Toggle sidebar" />
            <div id="app-header-left" class="min-w-0 max-w-full flex-1 overflow-x-auto flex items-center gap-2"></div>
            <Button v-if="headerStore.showBack" variant="ghost" class="gap-2" @click="headerStore.triggerBack">
              <ArrowLeft class="size-4" />{{ $t('common.back') }}
            </Button>
          </div>
          <div
            id="app-header-center"
            class="flex w-[min(42rem,calc(100vw-7rem))] min-w-0 max-w-full items-center justify-center gap-2"></div>
          <div id="app-header-right" class="relative z-10 flex shrink-0 items-center justify-self-end gap-2"></div>
        </header>
        <main class="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto"><RouterView /></main></SidebarInset
    ></SidebarProvider>
  </div>
  <div v-else class="grid min-h-dvh place-items-center text-muted-foreground">{{ $t('common.loading') }}...</div>
  <Dialog v-model:open="showSettings"
    ><DialogContent
      ><DialogHeader
        ><DialogTitle>{{ $t('common.settings') }}</DialogTitle></DialogHeader
      >
      <Tabs v-model="activeTab">
        <TabsList>
          <TabsTrigger value="general">{{ $t('settings.general') }}</TabsTrigger>
          <TabsTrigger value="cards">{{ $t('settings.cards') }}</TabsTrigger>
        </TabsList>
        <TabsContent value="general" class="grid gap-5 pt-4">
          <div class="grid gap-2">
            <Label for="interface-language">{{ $t('profile.interfaceLanguage') }}</Label>
            <Select
              id="interface-language"
              :model-value="interfaceLanguage"
              @update:model-value="(value) => userStore.updateUserSettings({interface_language: value as Language})"
              ><SelectTrigger><SelectValue /></SelectTrigger
              ><SelectContent
                ><SelectItem v-for="language in interfaceLanguages" :key="language" :value="language">{{
                  i18next.t(`common.${language}`)
                }}</SelectItem></SelectContent
              ></Select
            >
          </div>
          <div class="grid gap-2">
            <Label for="timezone">{{ $t('settings.timezone') }}</Label>
            <TimeZoneCombobox
              id="timezone"
              :model-value="timeZone"
              :options="timeZoneOptions"
              :placeholder="$t('settings.timezone')"
              :search-placeholder="$t('settings.timezoneSearch')"
              :empty-text="$t('settings.timezoneNotFound')"
              @update:model-value="updateTimeZone" />
            <p class="text-xs text-muted-foreground">{{ $t('settings.timezoneDescription') }}</p>
          </div>
          <div class="grid gap-2">
            <div class="flex items-center justify-between gap-4">
              <Label for="email-digest-paused">{{ $t('settings.emailDigestPaused') }}</Label>
              <Switch
                id="email-digest-paused"
                data-testid="settings-email-digest-paused"
                :model-value="emailDigestPaused"
                @update:model-value="updateEmailDigestPaused" />
            </div>
            <p class="text-xs text-muted-foreground">{{ $t('settings.emailDigestPausedDescription') }}</p>
          </div>
          <div class="grid gap-2">
            <Label for="theme-mode">{{ $t('settings.theme') }}</Label>
            <Select id="theme-mode" :model-value="localStore.theme" @update:model-value="updateThemeMode">
              <SelectTrigger data-testid="settings-theme-trigger" :aria-label="$t('settings.theme')"
                ><SelectValue
              /></SelectTrigger>
              <SelectContent>
                <SelectItem v-for="mode in ThemeModes" :key="mode" :value="mode">{{
                  $t(`settings.themeModes.${mode}`)
                }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </TabsContent>
        <TabsContent value="cards" class="grid gap-4 pt-4">
          <div class="flex items-center justify-between">
            <Label for="cards-auto-speak">{{ $t('study.autoSpeak') }}</Label
            ><Switch
              id="cards-auto-speak"
              data-testid="cards-setting-auto-speak"
              :model-value="localStore.cardsAutoSpeak"
              @update:model-value="(v: boolean) => (localStore.cardsAutoSpeak = v)" />
          </div>
          <div class="flex items-center justify-between">
            <Label for="cards-shuffle">{{ $t('study.shuffle') }}</Label
            ><Switch
              id="cards-shuffle"
              data-testid="cards-setting-shuffle"
              :model-value="localStore.cardsShuffle"
              @update:model-value="(v: boolean) => (localStore.cardsShuffle = v)" />
          </div>
          <div class="flex items-center justify-between">
            <Label for="cards-show-image-on-front" class="max-w-80">{{ $t('study.showImageOnFront') }}</Label
            ><Switch
              id="cards-show-image-on-front"
              data-testid="cards-setting-show-image-on-front"
              :model-value="localStore.cardsShowImageOnFront"
              @update:model-value="(v: boolean) => (localStore.cardsShowImageOnFront = v)" />
          </div>
          <div class="flex items-center justify-between">
            <Label for="cards-3d">{{ $t('study.flip3d') }}</Label
            ><Switch
              id="cards-3d"
              data-testid="cards-setting-3d"
              :model-value="localStore.cardsIs3d"
              @update:model-value="(v: boolean) => (localStore.cardsIs3d = v)" />
          </div>
          <div class="flex items-center justify-between">
            <Label for="cards-swipe">{{ $t('study.enableSwipe') }}</Label
            ><Switch
              id="cards-swipe"
              data-testid="cards-setting-swipe"
              :model-value="localStore.cardsEnableSwipe"
              @update:model-value="(v: boolean) => (localStore.cardsEnableSwipe = v)" />
          </div>
          <div v-if="localStore.cardsEnableSwipe" class="flex items-center justify-between">
            <Label for="cards-hide-nav">{{ $t('study.hideNavButtons') }}</Label
            ><Switch
              id="cards-hide-nav"
              data-testid="cards-setting-hide-nav"
              :model-value="localStore.cardsHideNav"
              @update:model-value="(v: boolean) => (localStore.cardsHideNav = v)" />
          </div>
          <div class="flex items-center justify-between">
            <Label for="cards-floating-controls" class="max-w-80">{{ $t('study.floatingControls') }}</Label
            ><Switch
              id="cards-floating-controls"
              data-testid="cards-setting-floating-controls"
              :model-value="localStore.cardsFloatingControls"
              @update:model-value="(v: boolean) => (localStore.cardsFloatingControls = v)" />
          </div>
          <div class="flex items-center justify-between gap-4">
            <Label for="cards-floating-controls-size" class="max-w-80">{{ $t('study.floatingControlsSize') }}</Label
            ><Select
              id="cards-floating-controls-size"
              :disabled="!localStore.cardsFloatingControls"
              :model-value="String(localStore.cardsFloatingControlsSize)"
              @update:model-value="updateFloatingControlsSize"
              ><SelectTrigger
                class="w-44"
                data-testid="cards-setting-floating-controls-size"
                :aria-label="$t('study.floatingControlsSize')"
                ><SelectValue /></SelectTrigger
              ><SelectContent
                ><SelectItem
                  v-for="size in CardsFloatingControlSizes"
                  :key="size"
                  :value="String(size)"
                  :data-testid="`cards-setting-floating-controls-size-${size}`"
                  >{{ $t('study.floatingControlsSizes.' + size) }}</SelectItem
                ></SelectContent
              ></Select
            >
          </div>
        </TabsContent>
      </Tabs>
      <DialogFooter
        ><Button data-testid="settings-submit" @click="showSettings = false">{{
          $t('common.ok')
        }}</Button></DialogFooter
      ></DialogContent
    ></Dialog
  >
</template>
